import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ahobilam.com';
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

// Simple SVG Line Chart
const LineChart = ({ data, width = 700, height = 250 }) => {
  if (!data || data.length === 0) return <div className="text-gray-400 text-center py-8">No data</div>;
  const maxVal = Math.max(...data.map(d => d.views), 1);
  const padding = { top: 20, right: 20, bottom: 40, left: 50 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const points = data.map((d, i) => ({
    x: padding.left + (i / Math.max(data.length - 1, 1)) * chartW,
    y: padding.top + chartH - (d.views / maxVal) * chartH,
    ...d
  }));

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaD = pathD + ` L ${points[points.length - 1].x} ${padding.top + chartH} L ${points[0].x} ${padding.top + chartH} Z`;

  // Y-axis labels
  const yLabels = [0, Math.round(maxVal / 2), maxVal];

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ maxHeight: height }}>
      <defs>
        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4F46E5" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#4F46E5" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {/* Grid lines */}
      {yLabels.map((v, i) => {
        const y = padding.top + chartH - (v / maxVal) * chartH;
        return (
          <g key={i}>
            <line x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="#e5e7eb" strokeDasharray="4" />
            <text x={padding.left - 8} y={y + 4} textAnchor="end" className="text-xs" fill="#9ca3af" fontSize="11">{v}</text>
          </g>
        );
      })}
      {/* Area fill */}
      <path d={areaD} fill="url(#areaGrad)" />
      {/* Line */}
      <path d={pathD} fill="none" stroke="#4F46E5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* Dots */}
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="4" fill="#4F46E5" stroke="white" strokeWidth="2" />
          {/* X-axis labels - show every 5th or if < 10 */}
          {(data.length <= 10 || i % Math.ceil(data.length / 8) === 0) && (
            <text x={p.x} y={height - 5} textAnchor="middle" fill="#9ca3af" fontSize="10">
              {p._id ? p._id.substring(5) : ''}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
};

// Simple Bar Chart
const BarChart = ({ data, width = 700, height = 300 }) => {
  if (!data || data.length === 0) return <div className="text-gray-400 text-center py-8">No data</div>;
  const maxVal = Math.max(...data.map(d => d.views), 1);
  const barWidth = Math.min(40, (width - 80) / data.length - 8);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ maxHeight: height }}>
      {data.map((d, i) => {
        const barH = (d.views / maxVal) * (height - 60);
        const x = 60 + i * ((width - 80) / data.length);
        const y = height - 40 - barH;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barWidth} height={barH} rx="4" fill={COLORS[i % COLORS.length]} opacity="0.85" />
            <text x={x + barWidth / 2} y={y - 5} textAnchor="middle" fill="#374151" fontSize="11" fontWeight="600">{d.views}</text>
            <text x={x + barWidth / 2} y={height - 8} textAnchor="middle" fill="#6b7280" fontSize="9" transform={`rotate(-30 ${x + barWidth / 2} ${height - 8})`}>
              {(d._id || '').replace(/^\//, '').substring(0, 15) || '/'}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// Donut Chart
const DonutChart = ({ data, size = 180 }) => {
  if (!data || data.length === 0) return <div className="text-gray-400 text-center py-8">No data</div>;
  const total = data.reduce((s, d) => s + d.count, 0);
  const cx = size / 2, cy = size / 2, r = size * 0.35, strokeW = size * 0.12;
  let cumAngle = -90;

  return (
    <div className="flex items-center gap-4">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {data.map((d, i) => {
          const angle = (d.count / total) * 360;
          const startAngle = cumAngle;
          cumAngle += angle;
          const startRad = (startAngle * Math.PI) / 180;
          const endRad = ((startAngle + angle) * Math.PI) / 180;
          const largeArc = angle > 180 ? 1 : 0;
          const x1 = cx + r * Math.cos(startRad), y1 = cy + r * Math.sin(startRad);
          const x2 = cx + r * Math.cos(endRad), y2 = cy + r * Math.sin(endRad);
          return (
            <path key={i} d={`M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`}
              fill="none" stroke={COLORS[i % COLORS.length]} strokeWidth={strokeW} strokeLinecap="round" />
          );
        })}
        <text x={cx} y={cy - 5} textAnchor="middle" fill="#1f2937" fontSize="22" fontWeight="bold">{total}</text>
        <text x={cx} y={cy + 14} textAnchor="middle" fill="#6b7280" fontSize="11">total</text>
      </svg>
      <div className="flex flex-col gap-1">
        {data.map((d, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
            <span className="text-gray-700">{d._id || 'unknown'}</span>
            <span className="text-gray-500 ml-auto font-medium">{d.count} ({Math.round(d.count / total * 100)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// Main Analytics Dashboard Component
// ══════════════════════════════════════════════════════════════
export const AnalyticsDashboard = () => {
  const [trends, setTrends] = useState(null);
  const [daily, setDaily] = useState([]);
  const [pages, setPages] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [visitorMeta, setVisitorMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [visitorDays, setVisitorDays] = useState(7);
  const [visitorPage, setVisitorPage] = useState(1);
  const [activeTab, setActiveTab] = useState('overview');

  const headers = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`
  }), []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [trendsRes, dailyRes, pagesRes, refRes, devRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/trends`, { headers: headers() }),
        axios.get(`${API}/admin/analytics/daily?days=${days}`, { headers: headers() }),
        axios.get(`${API}/admin/analytics/pages?days=${days}`, { headers: headers() }),
        axios.get(`${API}/admin/analytics/referrers?days=${days}`, { headers: headers() }),
        axios.get(`${API}/admin/analytics/devices?days=${days}`, { headers: headers() }),
      ]);
      setTrends(trendsRes.data);
      setDaily(dailyRes.data.daily || []);
      setPages(pagesRes.data.pages || []);
      setReferrers(refRes.data.referrers || []);
      setDevices(devRes.data.devices || []);
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [days, headers]);

  const fetchVisitors = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics/visitors?days=${visitorDays}&page_num=${visitorPage}`, { headers: headers() });
      setVisitors(res.data.visitors || []);
      setVisitorMeta(res.data);
    } catch (err) {
      console.error('Visitors fetch error:', err);
    }
  }, [visitorDays, visitorPage, headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (activeTab === 'visitors') fetchVisitors(); }, [activeTab, fetchVisitors]);

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics/export?days=${days}`, { headers: headers() });
      const data = res.data.data || [];
      if (data.length === 0) { alert('No data to export'); return; }
      const cols = Object.keys(data[0]);
      const csv = [cols.join(','), ...data.map(r => cols.map(c => `"${(r[c] || '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `analytics_${days}days_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (err) {
      alert('Export failed');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-gray-600">Loading analytics...</p>
      </div>
    </div>
  );

  const tabs = [
    { id: 'overview', label: '📊 Overview' },
    { id: 'pages', label: '📄 Pages' },
    { id: 'sources', label: '🔗 Sources' },
    { id: 'visitors', label: '👥 Visitors' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
          <h1 className="text-2xl font-bold text-gray-800">📊 Analytics Dashboard</h1>
          <div className="flex items-center gap-3">
            <select value={days} onChange={e => setDays(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button onClick={exportCSV} className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 font-medium">
              📥 Export CSV
            </button>
            <button onClick={fetchAll} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-indigo-700 font-medium">
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* Trend Cards */}
        {trends && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <TrendCard label="Today" value={trends.this_week?.views || 0} sub={`${trends.this_week?.unique || 0} unique`}
              change={trends.week_change} color="indigo" />
            <TrendCard label="This Week" value={trends.this_week?.views || 0} sub={`${trends.this_week?.unique || 0} unique`}
              change={trends.week_change} color="green" />
            <TrendCard label="This Month" value={trends.this_month?.views || 0} sub={`${trends.this_month?.unique || 0} unique`}
              change={trends.month_change} color="amber" />
            <TrendCard label="All Time" value={trends.all_time?.views || 0} sub={`${trends.all_time?.unique || 0} unique visitors`}
              color="purple" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-lg p-1 shadow-sm border">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Daily Views Chart */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">📈 Daily Page Views (Last {days} days)</h2>
              <LineChart data={daily} />
            </div>

            {/* Two columns: Device + Hourly */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h2 className="text-lg font-bold text-gray-800 mb-4">📱 Device Breakdown</h2>
                <DonutChart data={devices} />
              </div>
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h2 className="text-lg font-bold text-gray-800 mb-4">⏰ Hourly Distribution (This Month)</h2>
                {trends?.hourly_distribution && (
                  <div className="flex items-end gap-1" style={{ height: 160 }}>
                    {Array.from({ length: 24 }, (_, h) => {
                      const entry = trends.hourly_distribution.find(e => e._id === h);
                      const count = entry?.count || 0;
                      const maxH = Math.max(...trends.hourly_distribution.map(e => e.count), 1);
                      const barH = (count / maxH) * 140;
                      return (
                        <div key={h} className="flex flex-col items-center flex-1" title={`${h}:00 — ${count} views`}>
                          <div className="text-xs text-gray-500 mb-1">{count > 0 ? count : ''}</div>
                          <div className="w-full rounded-t" style={{ height: barH, backgroundColor: count > 0 ? '#6366f1' : '#e5e7eb', minHeight: 2 }} />
                          <div className="text-xs text-gray-400 mt-1">{h % 3 === 0 ? `${h}` : ''}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'pages' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">🏆 Top Pages</h2>
              <BarChart data={pages} />
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">📋 Page Details</h2>
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Page</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Views</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Unique</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Avg Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {pages.map((p, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm">
                        <span className="inline-block w-6 text-center text-xs font-bold text-gray-400 mr-2">{i + 1}</span>
                        <a href={`https://ahobilam.com${p._id}`} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">{p._id || '/'}</a>
                      </td>
                      <td className="py-3 px-4 text-sm text-right font-medium">{p.views}</td>
                      <td className="py-3 px-4 text-sm text-right">{p.unique_visitors}</td>
                      <td className="py-3 px-4 text-sm text-right">{p.avg_duration ? `${p.avg_duration}s` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'sources' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">🔗 Traffic Sources</h2>
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Source</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Visits</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Unique</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Bar</th>
                  </tr>
                </thead>
                <tbody>
                  {referrers.map((r, i) => {
                    const maxVisits = referrers[0]?.visits || 1;
                    return (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm font-medium">
                          {r._id === 'direct' ? '🔗 Direct' :
                            r._id?.includes('google') ? '🔍 Google' :
                              r._id?.includes('facebook') ? '📘 Facebook' :
                                r._id?.includes('whatsapp') ? '💬 WhatsApp' :
                                  r._id?.includes('t.co') ? '🐦 Twitter' : `🌐 ${r._id || 'unknown'}`}
                        </td>
                        <td className="py-3 px-4 text-sm text-right font-medium">{r.visits}</td>
                        <td className="py-3 px-4 text-sm text-right">{r.unique_visitors}</td>
                        <td className="py-3 px-4">
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div className="h-2 rounded-full" style={{ width: `${(r.visits / maxVisits) * 100}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'visitors' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <select value={visitorDays} onChange={e => { setVisitorDays(Number(e.target.value)); setVisitorPage(1); }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
                <option value={1}>Today</option>
                <option value={3}>Last 3 days</option>
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
              </select>
              <span className="text-sm text-gray-500">Total: {visitorMeta.total || 0} records</span>
            </div>

            <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Time</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">IP</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Page</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Referrer</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Device</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {visitors.map((v, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-4 text-gray-600 whitespace-nowrap">
                        {v.timestamp ? new Date(v.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td className="py-2 px-4 font-mono text-xs">{v.ip || '-'}</td>
                      <td className="py-2 px-4">
                        <a href={`https://ahobilam.com${v.page}`} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">{v.page || '/'}</a>
                      </td>
                      <td className="py-2 px-4 text-gray-500">{v.referrer_domain || v.referrer || 'direct'}</td>
                      <td className="py-2 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${v.device_type === 'mobile' ? 'bg-blue-100 text-blue-800' : v.device_type === 'tablet' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                          {v.device_type || 'desktop'}
                        </span>
                      </td>
                      <td className="py-2 px-4 text-right">{v.duration ? `${v.duration}s` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {visitorMeta.total_pages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                <button disabled={visitorPage <= 1} onClick={() => setVisitorPage(p => p - 1)}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50">← Prev</button>
                <span className="px-3 py-1 text-sm text-gray-600">Page {visitorPage} of {visitorMeta.total_pages}</span>
                <button disabled={visitorPage >= visitorMeta.total_pages} onClick={() => setVisitorPage(p => p + 1)}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50">Next →</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const TrendCard = ({ label, value, sub, change, color = 'indigo' }) => {
  const bg = { indigo: 'from-indigo-500 to-indigo-600', green: 'from-green-500 to-green-600', amber: 'from-amber-500 to-amber-600', purple: 'from-purple-500 to-purple-600' };
  return (
    <div className={`bg-gradient-to-br ${bg[color] || bg.indigo} rounded-xl p-5 text-white shadow-lg`}>
      <p className="text-sm opacity-80 mb-1">{label}</p>
      <p className="text-3xl font-bold">{value.toLocaleString()}</p>
      {sub && <p className="text-xs opacity-70 mt-1">{sub}</p>}
      {change !== undefined && (
        <p className={`text-xs mt-2 font-medium ${change >= 0 ? 'text-green-200' : 'text-red-200'}`}>
          {change >= 0 ? '↑' : '↓'} {Math.abs(change)}% vs last period
        </p>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
